import React, { FC, Fragment, useEffect, useState } from 'react';

import { Button, Checkbox, Form, Row, Select } from 'antd';
import { useIntl } from 'react-intl';
import {
  CustomInputText,
  CustomInputTextArea,
  CustomOnlyNumber,
  CustomSelector,
} from 'src/components/forms/pin/CustomInputText';
import { ComboData, IAddressData, IAddressDataPeru } from 'src/shared/models';
import {
  customFormatMessage,
  customRegexName,
  phoneRegex,
  useWindowSize,
  dniPeru,
  directionCharacters,
} from 'utils';
import { IAddressFormValues } from 'src/components/forms/interfaces';

interface Props {
  address?: IAddressDataPeru;
  isEditForm?: boolean;
  isFirstAddress?: boolean;
  onCreateAddress?: (address: IAddressData) => void;
  onUpdateAddress?: (address: IAddressData) => void;
  regionList: ComboData[];
  departmentList: ComboData[];
  provinceList: ComboData[];
  districtList: ComboData[];
  isLoading?: boolean;
}

const AddressFormPe: FC<Props> = ({
  address,
  isEditForm,
  isFirstAddress,
  onCreateAddress,
  onUpdateAddress,
  regionList,
  departmentList,
  provinceList,
  districtList,
  isLoading = false,
}): JSX.Element => {
  const [form] = Form.useForm();
  const { isMD } = useWindowSize();
  const { formatMessage } = useIntl();
  const [addressData, setAddressData] = useState(address);
  const [provinceListFiltered, setProvinceListFiltered] = useState<ComboData[]>(
    [],
  );
  const [districtListFiltered, setDistrictListFiltered] = useState<ComboData[]>(
    [],
  );
  const departmentListFiltered = departmentList;

  const formValues: Partial<IAddressDataPeru> = {
    name: address?.name || '',
    surname: address?.surname || '',
    address: address?.address.includes('#')
      ? address.address.substring(0, address.address.indexOf('#'))
      : address?.address || '',
    addressOptionalInfo: address?.address.includes('#')
      ? address?.address.substring(address.address.indexOf('#') + 1)
      : '',
    phone: address?.phone || '',
    zipCode: address?.zipCode || '',
    city: address?.city || '',
    regionName: address?.regionName || '',
    deliveryInstructions: address?.deliveryInstructions || '',
    district: address?.district || '',
  };

  let formFields: IAddressFormValues[] = [
    {
      type: 'text',
      name: 'name',
      placeholder: 'form.field.name',
      handleChange: 'name',
      initialValue: formValues.name,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.name' }) },
        ),
      },
      autocomplete: 'given-name',
      length: 25,
    },
    {
      type: 'text',
      name: 'surname',
      placeholder: 'form.field.last-name',
      handleChange: 'surname',
      initialValue: formValues.surname,
      rules: {
        pattern: customRegexName,
        message: formatMessage(
          { id: 'form.field.error-only-letters' },
          { name: formatMessage({ id: 'form.field.last-name' }) },
        ),
      },
      autocomplete: 'family-name',
      length: 50,
    },
    {
      type: 'text',
      name: 'address',
      placeholder: 'form.field.address',
      handleChange: 'address',
      initialValue: formValues.address,
      rules: {
        pattern: directionCharacters,
        message: formatMessage({ id: 'form.field.error-restricted-error' }),
      },
      autocomplete: 'street-address',
      length: 40,
    },
    {
      type: 'optionalText',
      name: 'addressOptionalInfo',
      placeholder: 'form.field.addressOptionalInfo',
      handleChange: 'addressOptionalInfo',
      initialValue: formValues.addressOptionalInfo,
      rules: {
        pattern: directionCharacters,
        message: formatMessage({ id: 'form.field.error-restricted-error' }),
      },
      autocomplete: 'address-line1',
      length: 20,
    },
    {
      type: 'number',
      name: 'phone',
      placeholder: 'form.field.telephone',
      handleChange: 'phone',
      initialValue: formValues.phone,
      rules: {
        pattern: phoneRegex.PE,
        message: formatMessage({
          id: 'form.field.phone-error-number-pe',
        }),
      },
      autocomplete: 'tel',
      length: 9,
    },
    {
      type: 'citySelector',
      name: 'city',
      placeholder: 'form.field.city',
      handleChange: 'city',
      initialValue: formValues.city,
    },
    {
      type: 'regionSelector',
      name: 'regionName',
      placeholder: 'form.field.province',
      handleChange: 'regionName',
      initialValue: formValues.regionName,
    },
    {
      type: 'districtSelector',
      name: 'district',
      placeholder: 'form.field.district',
      handleChange: 'district',
      initialValue: formValues.district,
    },
  ];

  let formFieldsNext: IAddressFormValues[] = [
    {
      type: 'number',
      name: 'zipCode', // Why the DNI is saved as zipCode?
      placeholder: 'form.field.dni',
      handleChange: 'zipCode',
      initialValue: formValues.zipCode,
      rules: {
        pattern: dniPeru,
        message: formatMessage(
          { id: 'form.field.dni-error' },
          { name: formatMessage({ id: 'form.field.dni' }) },
        ),
      },
      length: 8,
    },
    {
      type: 'optionalTextArea',
      name: 'deliveryInstructions',
      placeholder: 'form.field.deliveryInstructions',
      handleChange: 'deliveryInstructions',
      initialValue: '',
      rules: {
        pattern: directionCharacters,
        message: formatMessage({ id: 'form.field.error-restricted-error' }),
      },
    },
  ];

  useEffect(() => {
    checkCanContinue();
  });

  useEffect(() => {
    form.resetFields();
    if (!addressData) return;
    form.setFieldsValue(formValues);
    setAddressData((data) => {
      return {
        ...data,
        address: formValues.address,
        addressOptionalInfo: formValues.addressOptionalInfo,
      };
    });
  }, []);

  useEffect(() => {
    if (!isFirstAddress) return;

    setAddressData((currentAddress) => {
      return {
        ...currentAddress,
        defaultAddress: true,
      };
    });
  }, []);

  const isDefaultAddressChecked = (): boolean => {
    if ((!isEditForm && isFirstAddress) || address?.defaultAddress) {
      return true;
    }
    return false;
  };

  const isDefaultAddressDisabled = () => {
    const editingDefaultAddress = Boolean(isEditForm && address.defaultAddress);

    if (isFirstAddress || editingDefaultAddress) return true;
    return false;
  };

  const getCompleteAdressData = () => {
    return addressData.addressOptionalInfo
      ? `${addressData.address}#${addressData.addressOptionalInfo}`
      : addressData.address;
  };

  const handleSubmit = () => {
    let completeAddressData = getCompleteAdressData();
    let newAddress = { ...addressData, address: completeAddressData };
    isEditForm ? onUpdateAddress(newAddress) : onCreateAddress(newAddress);
    form.resetFields();
  };

  const checkAddressForm = (addressData: IAddressDataPeru): boolean => {
    return Boolean(
      addressData?.name?.match(customRegexName) &&
        addressData?.surname?.match(customRegexName) &&
        addressData?.address &&
        addressData?.phone?.match(phoneRegex.PE) &&
        addressData?.zipCode?.match(dniPeru) &&
        addressData?.city &&
        addressData?.regionName &&
        addressData?.district,
    );
  };

  const checkCanContinue = () => {
    const isDataCorrect = checkAddressForm(addressData);
    if (addressData?.canContinue == isDataCorrect) return;
    setAddressData((data) => {
      return {
        ...data,
        canContinue: isDataCorrect,
      };
    });
  };

  const filterProvincesByDepartment = (event: string) => {
    setDistrictListFiltered([]);
    let provinceListFilter: ComboData[] = [];
    for (let i = 1; i < provinceList?.length; i++) {
      for (let j = 1; j < provinceList?.length; j++) {
        if (`${event}.${i}` === provinceList[j].value) {
          provinceListFilter?.push(provinceList[j]);
        }
      }
    }
    setProvinceListFiltered(provinceListFilter);
  };

  const filterDisctrictsByProvince = (event: string) => {
    let districtListFilter: ComboData[] = [];
    for (let i = 1; i < districtList?.length; i++) {
      for (let j = 1; j < districtList?.length; j++) {
        if (`${event}.${i}` === districtList[j].value) {
          districtListFilter?.push(districtList[j]);
        }
      }
    }
    setDistrictListFiltered(districtListFilter);
  };

  const handleChange = (field: string) => (event: any) => {
    if (event.persist) event.persist();
    setAddressData((data) => {
      const newData = { ...data };
      let value = event?.target?.value;
      switch (field) {
        case 'phone':
        case 'zipCode':
          value = event;
          break;
        case 'defaultAddress':
          value = event.target.checked;
          break;
        case 'city':
          form.resetFields(['regionName', 'district']);
          filterProvincesByDepartment(event);
          setDistrictListFiltered([]);
          value = event;

          return {
            ...newData,
            [field]: value ?? null,
            regionName: null,
            district: null,
          };
        case 'regionName':
          form.resetFields(['district']);
          filterDisctrictsByProvince(event);
          value = event;
          return { ...newData, [field]: value, district: null };
        case 'district':
          value = event;
          break;
        default:
          value = event.target.value;
          break;
      }
      return { ...newData, [field]: value };
    });
  };

  const renderTitle = (
    <>
      {isMD && (
        <h4 className="address__form-title">
          {isEditForm
            ? formatMessage({ id: 'form.address.edit-title' })
            : formatMessage({ id: 'form.address.new-title' })}
        </h4>
      )}
      {!isMD && !isEditForm && (
        <h4 className="address__form-title">
          {formatMessage({ id: 'form.address.new-title' })}
        </h4>
      )}
    </>
  );

  const renderFields = formFields.map((field) => {
    let component: JSX.Element;
    let disabled = false;

    switch (field.type) {
      case 'text':
        component = CustomInputText(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length,
          true,
          [field.rules],
          null,
          field.autocomplete,
        );
        break;
      case 'optionalText':
        component = CustomInputText(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length,
          false,
          [field.rules],
          null,
          field.autocomplete,
        );
        break;
      case 'number':
        component = CustomOnlyNumber(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length ?? 40,
          true,
          [field.rules],
          null,
          field.autocomplete,
        );
        break;
      case 'citySelector': // Departamento
        if (addressData?.city && !provinceListFiltered?.length)
          filterProvincesByDepartment(addressData.city);
        component = CustomSelector(
          form,
          field.name,
          departmentListFiltered,
          field.placeholder,
          'value',
          handleChange(field.name),
          false,
          'description',
          true,
          null,
          false,
        );
        break;
      case 'regionSelector': // Provincia
        if (addressData?.regionName && !districtListFiltered?.length)
          filterDisctrictsByProvince(addressData.regionName);
        disabled = !addressData?.city;
        component = CustomSelector(
          form,
          field.name,
          provinceListFiltered,
          field.placeholder,
          'value',
          handleChange(field.name),
          false,
          'description',
          true,
          null,
          false,
          disabled,
        );
        break;
      case 'districtSelector': // Distrito
        disabled = !addressData?.regionName || !addressData?.city;
        component = CustomSelector(
          form,
          field.name,
          districtListFiltered,
          field.placeholder,
          'value',
          handleChange(field.name),
          false,
          'description',
          true,
          null,
          false,
          disabled,
        );
        break;
      default:
        return;
    }

    return <Fragment key={field.name}>{component}</Fragment>;
  });

  const renderFieldsNext = formFieldsNext.map((field) => {
    let component: JSX.Element;

    switch (field.type) {
      case 'number':
        component = CustomOnlyNumber(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          field.length ?? 40,
          true,
          [field.rules],
          null,
          field.autocomplete,
        );
        break;
      case 'optionalTextArea':
        component = CustomInputTextArea(
          form,
          field.name,
          field.placeholder,
          handleChange(field.name),
          field.initialValue,
          500,
          false,
          [field.rules],
          null,
          field.autocomplete,
        );
        break;
      default:
        return;
    }

    return <Fragment key={field.name}>{component}</Fragment>;
  });

  return (
    <>
      {renderTitle}
      <Form
        name="addressForm"
        form={form}
        className="address__form"
        onFinish={handleSubmit}>
        {renderFields}
        {renderFieldsNext}
        {!isDefaultAddressDisabled() && (
          <Form.Item
            name="defaultAddress"
            valuePropName="checked"
            initialValue={isDefaultAddressChecked()}>
            <Checkbox
              className="address__form__button-wrapper"
              defaultChecked={isDefaultAddressChecked()}
              onChange={handleChange('defaultAddress')}
              disabled={isDefaultAddressDisabled()}>
              {formatMessage({ id: 'form.field.address.preferred' })}
            </Checkbox>
          </Form.Item>
        )}
        <Form.Item shouldUpdate className="address__form__button-wrapper">
          {() => (
            <Button
              htmlType="submit"
              className="address__form__button-submit btn-primary btn-primary--black"
              disabled={!addressData?.canContinue || isLoading}>
              {formatMessage({ id: 'form.address.add-new' })}
              {isMD && (
                <i className="icon icon--arrow-normal-right-white button-icon" />
              )}
            </Button>
          )}
        </Form.Item>
      </Form>
    </>
  );
};

export default AddressFormPe;
